import { useMemo } from 'react';

import type { TimelineNotesInput } from './loggerContext';
import type { TimelineRow } from './timelineRows';
import { buildTimelineFeed, type FeedItem } from './timelineFeed';
import { placeCompletedSetAtTail } from './timelinePlacement';
import { uuidv4 } from './uuid';

// Note-event surface for the timeline logger (Solution C): merges set rows +
// note events into one chronological feed and owns the composer handlers.
// Kept separate from useTimelineLogger (set/focus logic) so each stays focused
// + under budget. Notes live on the draft (draft.noteEvents) and persist at
// workout save — never per-tap (no per-click cloud writes).

export function useTimelineNotes(
  ctx: TimelineNotesInput,
  setRows: readonly TimelineRow[],
  activeExerciseId: string | null,
) {
  const { draft, dispatch, mode } = ctx;

  const feed: FeedItem[] = useMemo(
    () => buildTimelineFeed(setRows, draft.noteEvents ?? []),
    [setRows, draft.noteEvents],
  );

  // Append a note. performedAt: live logging stamps the wall clock; a past-
  // session edit places it in-window-at-tail (never the live clock — the same
  // integrity rule a logged set follows, timelinePlacement). Returns false on
  // a too-short note so the composer keeps the text (schema floor = 4 chars).
  // The CLOCK question, so it names 'log-past' exactly (see loggerMode.ts).
  const onSendNote = (text: string): boolean => {
    const trimmed = text.trim();
    if (trimmed.length < 4) return false;
    const performedAt = mode === 'log-past'
      ? placeCompletedSetAtTail(draft.entries, draft.startedAt)
      : new Date().toISOString();
    dispatch({
      type: 'add_note_event',
      note: {
        id: uuidv4(),
        text: trimmed,
        performedAt,
        ...(activeExerciseId ? { exerciseContextId: activeExerciseId } : {}),
      },
    });
    return true;
  };

  const onDeleteNote = (id: string) => {
    dispatch({ type: 'remove_note_event', id });
  };

  return { feed, onSendNote, onDeleteNote };
}
